import React from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon, WarningAmber as WarningAmberIcon, GridViewOutlined as PlanogramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { colors } from '../../themes/typography';
import RecentSalesSection from './RecentSalesSection';
import { prefetchRecentSales } from '../../hooks/useRecentSales';
import openAlertInPlanogram from '../../utils/openAlertInPlanogram';

// Same width as the planogram slot drawer, so the sales table lays out identically in both
const PANEL_WIDTH = 460;

const closeSidebar = () => {
  window.currentPageSidebar = null;
  window.dispatchEvent(new Event('sidebarUpdate'));
};

/**
 * Right sidebar on the alert pages: the alert's latest sales at a glance, without leaving the page.
 * `alert` is a raw /alerts or /alerts-management row.
 */
const AlertSalesPanel = ({ alert, clientId, defaultScope, onOpenPlanogram }) => {
  const { t } = useTranslation();
  const location = useLocation();

  // The global sidebar outlives the page that opened it; close it when the user navigates away
  const openedAt = React.useRef(location.pathname);
  React.useEffect(() => {
    if (location.pathname !== openedAt.current) closeSidebar();
  }, [location.pathname]);

  const lanes = (alert.selection_code || []).join(', ');
  const machineLine = [
    `${t('machine')} ${alert.machine_id}`,
    lanes && `${t('machines_recentSalesLane')} ${lanes}`
  ].filter(Boolean).join(' • ');
  const placeLine = [alert.group, alert.location].filter(Boolean).join(' · ');

  return (
    <Box data-testid="alert-sales-panel">
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${colors.border.light}`,
        backgroundColor: colors.background.surface
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: colors.text.primary }}>
            {alert.product_name || '—'}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: colors.text.muted }}>{machineLine}</Typography>
          {placeLine && (
            <Typography noWrap sx={{ fontSize: '12px', color: colors.text.muted }}>{placeLine}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={closeSidebar} aria-label={t('close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {alert.weeks_since ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, pt: 1.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: '#ef4444' }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#ef4444' }}>
            {t('machines_alertNoSalesSince', { weeks: alert.weeks_since })}
          </Typography>
        </Box>
      ) : null}

      <RecentSalesSection
        storeId={alert.d_store_id}
        productId={alert.d_product_id}
        clientId={clientId}
        defaultScope={defaultScope}
      />

      <Box sx={{ px: 2, pb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<PlanogramIcon fontSize="small" />}
          onClick={onOpenPlanogram}
          sx={{ textTransform: 'none' }}
        >
          {t('alerts_openInPlanogram')}
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Opens the alert sales panel in the global right sidebar. Both the product's and the whole
 * machine's sales are fetched right away so either toggle shows instantly.
 * `scope` is the view it opens on: 'product' (default) or 'machine'.
 */
export const openAlertSalesPanel = (alert, navigate, clientId, scope = 'product') => {
  prefetchRecentSales(clientId, alert.d_store_id, alert.d_product_id).catch(() => {});
  prefetchRecentSales(clientId, alert.d_store_id, null).catch(() => {});

  window.currentPageSidebar = {
    overlayMode: true,
    width: PANEL_WIDTH,
    content: (
      <AlertSalesPanel
        key={`${alert.id}-${scope}`}
        alert={alert}
        clientId={clientId}
        defaultScope={scope}
        onOpenPlanogram={() => {
          closeSidebar();
          openAlertInPlanogram(alert, navigate, clientId);
        }}
      />
    )
  };
  window.dispatchEvent(new Event('sidebarUpdate'));
};

export default AlertSalesPanel;
